import React, { useState } from 'react';
import { App, Button, Flex, Input, Modal, Typography } from 'antd';
import { Field, Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { getPrerenderUser } from '../../actions/prerenderUserActions';
import { NewClearPartialCache } from './NewClearPartialCache';
import { NewClearPartialCacheModal } from './NewClearPartialCacheModal';
import { useLazyGetURLsQuery, useClearUrlCacheMutation } from '../urls/api/urlsApiSlice';
import { useEvent } from '../events/hooks/useEvent';
import { useCharbeePlanId } from '../../hooks/usePrerenderUser';
import { useClearCacheMutation } from '../cache-manager/CacheManagerApi';

export type ClearCacheStatus = {
  status: 'clear_pages_queued' | 'clear_pages_in_progress';
};

type ClearCacheResult = {
  text: string;
  type: 'success' | 'error';
};

const getResultModalText = (status?: ClearCacheStatus['status']): ClearCacheResult => {
  switch (status) {
    case 'clear_pages_queued':
      return { text: 'Clear cache scheduled', type: 'success' };
    case 'clear_pages_in_progress':
      return { text: 'Another cache clear is already in progress', type: 'error' };
    default:
      return { text: 'Unknown error occurred. Please try again later.', type: 'error' };
  }
};

type NewClearCacheModalProps = {
  open: boolean;
  onClose: () => void;
};

const FullCacheForm: React.FC<{
  onSubmit: (values: { keyword: string }) => void;
  onClose: () => void;
}> = ({ onSubmit, onClose }) => (
  <Form<{ keyword: string }>
    onSubmit={(values) => {
      onSubmit(values);
      onClose();
    }}
    validate={(values) => {
      const errors: { keyword?: string } = {};
      if (values.keyword !== 'clear cache') {
        errors.keyword = 'keyword mismatch';
      }
      return errors;
    }}
  >
    {({ handleSubmit, submitting, valid }) => (
      <form onSubmit={handleSubmit}>
        <Flex justify="start" style={{ paddingTop: 16 }}>
          <Typography.Text>
            To delete your entire cache, please type <u>clear cache</u> into the box below.
          </Typography.Text>
        </Flex>
        <Flex justify="space-between" style={{ paddingTop: 16, width: '100%' }} gap={16}>
          <Field name="keyword">
            {({ input, meta }) => (
              <Input
                {...input}
                type="text"
                placeholder="clear cache"
                className={`${meta.error && meta.touched ? 'border-danger' : ''}`}
                id="keyword"
              />
            )}
          </Field>
          <Button disabled={submitting || !valid} htmlType="submit" type="primary" danger>
            Delete All Cache
          </Button>
        </Flex>
      </form>
    )}
  </Form>
);

const DefaultView: React.FC<{
  onPartialCacheClick: (value: string) => void;
  onFullCacheClick: () => void;
  onClose: () => void;
  urlsNotFound: boolean;
  setUrlsNotFound: (urlsNotFound: boolean) => void;
}> = ({ onPartialCacheClick, onFullCacheClick, onClose, urlsNotFound, setUrlsNotFound }) => (
  <div>
    <NewClearPartialCache
      onClearPartialCacheClick={onPartialCacheClick}
      urlsNotFound={urlsNotFound}
      setUrlsNotFound={setUrlsNotFound}
    />
    <hr className="mt-5" />
    <FullCacheForm onSubmit={onFullCacheClick} onClose={onClose} />
  </div>
);

export const NewClearCacheModal: React.FC<NewClearCacheModalProps> = ({ open, onClose }) => {
  const limit = 10;
  const { message } = App.useApp();
  const [view, setView] = useState<'default' | 'partial'>('default');
  const [wildcard, setWildcard] = useState('');
  const [urlsNotFound, setUrlsNotFound] = useState(false);
  const [clearUrlCache, clearUrlCacheResult] = useClearUrlCacheMutation();
  const [clearCache] = useClearCacheMutation();
  const [getUrls, urlsResult] = useLazyGetURLsQuery();
  const { track } = useEvent();
  const chargebeePlanId = useCharbeePlanId();
  const dispatch = useDispatch();

  const handleClearCache = () => {
    clearCache()
      .unwrap()
      .then((data) => {
        const resultModalText = getResultModalText(data.status);
        message
          .open({
            content: resultModalText.text,
            type: resultModalText.type,
          })
          .then(() => {
            onClose();
            dispatch(getPrerenderUser());
          });
      })
      .catch((_error) => {
        const resultModalText = getResultModalText(); // fallback message
        message.open({
          content: resultModalText.text,
          type: resultModalText.type,
        });
      });
  };

  const handleBack = () => {
    setUrlsNotFound(false);
    setView('default');
    setWildcard('');
  };

  const handlePartialCacheSubmit = () => {
    clearUrlCache(wildcard)
      .unwrap()
      .then(() => {
        message.success('Clear partial cache scheduled');
        handleBack();
        track('Cache Partially Cleared', {
          subscription_plan: chargebeePlanId,
        });
      })
      .finally(() => {
        onClose();
        dispatch(getPrerenderUser());
      });
  };

  const onPartialClearClicked = async (value: string) => {
    setUrlsNotFound(false);
    const result = await getUrls({ wildcard: value, limit }).unwrap();
    if (result.count === 0) {
      setUrlsNotFound(true);
      return;
    }
    setWildcard(value);
    setView('partial');
  };

  const getModalTitle = () => {
    switch (view) {
      case 'default':
        return 'Clear Cache';
      case 'partial':
        return 'Clear Partial Cache';
    }
  };

  const renderFooter = () => {
    if (view === 'default') {
      return (
        <Flex justify="start" style={{ marginTop: 16 }}>
          <Button onClick={onClose}>Close</Button>
        </Flex>
      );
    }
    return (
      <Flex justify="space-between" style={{ marginTop: 16 }}>
        <Button onClick={handleBack}>Back</Button>
        <Button
          type="primary"
          onClick={handlePartialCacheSubmit}
          disabled={!urlsResult.data?.count || clearUrlCacheResult.isLoading || urlsResult.isFetching}
          loading={clearUrlCacheResult.isLoading || urlsResult.isFetching}
        >
          Clear Cache
        </Button>
      </Flex>
    );
  };

  const renderContent = () => {
    switch (view) {
      case 'default':
        return (
          <DefaultView
            onPartialCacheClick={onPartialClearClicked}
            onFullCacheClick={handleClearCache}
            onClose={onClose}
            urlsNotFound={urlsNotFound}
            setUrlsNotFound={setUrlsNotFound}
          />
        );
      case 'partial':
        return <NewClearPartialCacheModal urlsResult={urlsResult} clearUrlCacheResult={clearUrlCacheResult} />;
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      open={open}
      footer={renderFooter()}
      cancelText="Close"
      width={700}
      onCancel={onClose}
      destroyOnHidden
    >
      {renderContent()}
    </Modal>
  );
};
